import React, {useCallback, useEffect, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {
  Avatar,
  Body,
  Checkbox,
  designSystemToken,
  Icon,
} from '@lightricks/react-design-system';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import {buttonNames, screenNames} from '@/lib/delta/deltaConstants.js';
import checkImageValid from '@/utils/checkImageValid';
import getCreatorProfilePageUrl from '@/utils/getCreatorProfilePageUrl';
import translate from '@/utils/translate';
import type Profile from '@/types/profile';
import OffPlatformBadge from '@/components/off-platform-badge/OffPlatformBadge';
import ProfilePopup from '@/components/profile/ProfilePopup';
import styles from './UsernameCell.module.scss';

const RATING_BADGE_THRESHOLD = 4;
const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';

type UsernameCellProps = {
  profileId: string;
  picture: string;
  name: string;
  rating?: number | null;
  showRatingBadge?: boolean;
  isSelected: boolean;
  onSelectionChange?: (id: string, checked: boolean) => void;
  onCheckboxChange?: (checked: boolean) => void;
  profile?: Profile;
  showProfilePopup?: boolean;
  email?: string;
  /** When false, the selection checkbox is hidden (e.g. when a dedicated select column is used). Default true. */
  showSelectCheckbox?: boolean;
  isOffPlatform?: boolean;
};

function UsernameCell(props: UsernameCellProps) {
  const {
    profileId,
    picture,
    name,
    rating,
    showRatingBadge: showRatingBadgeProp = false,
    isSelected,
    onSelectionChange,
    onCheckboxChange,
    profile,
    email,
    showProfilePopup = false,
    showSelectCheckbox = true,
    isOffPlatform = false,
  } = props;
  const showRatingBadge =
    showRatingBadgeProp && rating != null && rating > RATING_BADGE_THRESHOLD;
  const [isHovered, setIsHovered] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setIsHovered(false);
  };

  const handleCheckboxChangeStable = useCallback(
    (checked: boolean) => {
      if (onSelectionChange) {
        onSelectionChange(profileId, checked);
      } else {
        onCheckboxChange?.(checked);
      }
    },
    [profileId, onSelectionChange, onCheckboxChange]
  );

  const handleCheckboxChange = (checked: boolean) => {
    handleCheckboxChangeStable(checked);

    if (checked) {
      AnalyticsService.dispatchEvent(eventNames.BUTTON_PRESSED, {
        button_name: buttonNames.MY_CREATORS.CREATOR_MARK,
        screen_name: screenNames.MY_CREATORS.MY_CREATORS,
        creator_id: profileId,
        flow_id: '',
        flow_name: '',
        triggered_flow_id: '',
        triggered_flow_name: '',
        screen_presentation_id: uuid(),
        tab: '',
        campaign_id: '',
      });
    }
  };

  const [pictureUrlValid, setPictureUrlValid] = useState(true);
  useEffect(() => {
    checkImageValid(picture, setPictureUrlValid);
  }, [picture]);

  return (
    <div className={styles.usernameCell}>
      {showSelectCheckbox && (
        <div
          className={styles.checkboxWrapper}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={handleCheckboxChange}
            customCheckedIcon={<img src={checkboxCheckedSrc} alt="" />}
            customUncheckedIcon={<img src={checkboxUncheckedSrc} alt="" />}
          />
        </div>
      )}
      <div className={styles.avatarWrapper}>
        <Avatar
          size="sm"
          src={pictureUrlValid ? picture : ''}
          type={pictureUrlValid && picture ? 'picture' : 'placeholder'}
          variant="circular"
          data-testid="avatar"
          background={designSystemToken('semantic.bg.tertiary')}
        />
        {showRatingBadge && (
          <span
            className={styles.ratingBadge}
            title={translate('components.creator-card.highly-rated')}
            aria-hidden
          >
            <Icon
              name="Navigation-Star-Fill"
              size="small"
              appearance="inverse"
              className={styles.ratingBadgeIcon}
            />
          </span>
        )}
        {isOffPlatform ? (
          <OffPlatformBadge variant="icon" className={styles.offPlatformMark} />
        ) : null}
      </div>
      <div
        className={styles.nameContainer}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className={styles.nameRow}>
          <Body size="md" className={styles.nameText}>
            {name}
          </Body>
        </div>

        {profile && showProfilePopup && (
          <ProfilePopup
            isVisible={isHovered}
            profile={profile}
            anchorEl={anchorEl}
            onClose={handleMouseLeave}
            email={email}
            getCreatorProfilePageUrlFunc={
              getCreatorProfilePageUrl.fromCreatorsList
            }
          />
        )}
      </div>
    </div>
  );
}

export default UsernameCell;
